import React, { Fragment, useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Table, TableHead, TableRow, TableCell, styled, tableCellClasses, useTheme } from '@mui/material';
import { Link, withRouter, useLocation } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import './ReviewFormsTable.scss';
import { ItemProps, ReviewRequiredForm, formTypes } from './ReviewFormsTypes';
import { CONSTANTS, TABLE_HEADERS, mapFormData, formatLocation, generateFormUrl } from './utils';
import { NavigationContext } from 'op-contexts';
import { DropDownField } from 'shared-components/components/FormFields';
import { LoadingSpinner } from 'shared-components/components';
import Pagination, { PageChangePayload } from './ReviewFormsPagination';
import { isUs } from 'op-utils/helpers';
import { FeatureOption } from 'op-interfaces';

const FORMS_IN_REVIEW_QUERY = gql`
  query PatientSearch($start: Int, $limit: Int, $location: String) {
    reviewRequiredPatients(start: $start, limit: $limit, location: $location) {
      totalCount
      forms {
        id
        firstName
        lastName
        patientId
        ida
        horizonCenterId
        status
        overallStatus
        formType
        updatedAt
        hasDataConflicts
      }
    }
  }
`;

const ALLOWED_LOCATION_QUERY = gql`
  query AllowedLocations($roles: [String]!, $hasOther: Boolean!) {
    allowedLocations(roles: $roles, hasOther: $hasOther) {
      id
      alias
    }
    featureOptions {
      id
      name
      description
      active
    }
  }
`;

const { BLANK, DEFAULT_PAGINATION_LIMIT, NO_PATIENTS_MESSAGE, REVIEW_FORMS, SELECT_ALL } = CONSTANTS;
const SELECT_ALL_ID = '';
const BLANK_ID = '-1';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.common.black,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: theme.palette.common.black,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const ReviewFormsTable = (): JSX.Element => {
  const location = useLocation();
  const navigationContext = useContext(NavigationContext);
  const theme = useTheme();

  // Location query
  const { data: locations } = useQuery(ALLOWED_LOCATION_QUERY, {
    variables: {
      roles: ['PSO'],
      hasOther: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Forms query using useLazyQuery for better control
  const [getFormsQuery, { data: formData, loading, error }] = useLazyQuery(FORMS_IN_REVIEW_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [locationFilterId, setLocationFilterId] = useState<string | undefined>(() => {
    return sessionStorage.getItem('locationId') || undefined;
  });
  const [pagination, setPagination] = useState<PageChangePayload>({
    currentPage: 1,
    limit: DEFAULT_PAGINATION_LIMIT,
    startingRecord: 0,
    endingRecord: DEFAULT_PAGINATION_LIMIT * 1,
  });

  // Extract data
  const forms = formData?.reviewRequiredPatients?.forms || [];
  const totalCount = formData?.reviewRequiredPatients?.totalCount || 0;
  const allowedLocations = locations?.allowedLocations || [];
  const featureOptions = locations?.featureOptions || [];
  const showNewRego = featureOptions.find(
    (featureOption: FeatureOption) => featureOption.name === 'NewAusRego' && featureOption.active,
  );

  // Helper function to get location display name from ID
  const getLocationString = (locationId: string | undefined): string => {
    if (!locationId) return '';

    const location = allowedLocations.find((item: any) => item.id === locationId);
    return location ? location.alias : '';
  };

  // When PSO clicks on a form for review, the "entrypoint" into the patient registration forms will be set to the
  // URL which corresponds to the "Review Forms" dashboard
  useEffect(() => {
    navigationContext.setRegEntryPath(location.pathname);
  }, [location.pathname, navigationContext]);

  useEffect(() => {
    const { startingRecord, limit } = pagination;

    // Only fetch if we have a valid locationFilterId or if it's the initial load
    if (locationFilterId !== undefined || !sessionStorage.getItem('locationId')) {
      getFormsQuery({
        variables: {
          start: startingRecord,
          limit,
          location: locationFilterId,
        },
      });
    }
  }, [pagination, locationFilterId, getFormsQuery]);

  const handlePaginationChange = (payload: PageChangePayload) => {
    setPagination(payload);
  };

  const handleLocationFilterChange = (location: string, locationId: string): void => {
    sessionStorage.setItem('locationId', locationId);
    setLocationFilterId(locationId);

    // Reset pagination to first page when location filter changes
    setPagination({
      currentPage: 1,
      limit: pagination.limit,
      startingRecord: 0,
      endingRecord: pagination.limit * 1,
    });
  };

  const renderLocationFilter = (): JSX.Element => {
    const locationListItems = allowedLocations.map((item: any): any => {
      return { id: item.id, name: item.alias, appKey: item.alias };
    });
    // Check if any patients don't have a location
    const blankLocationFound = forms.map((form: ReviewRequiredForm): boolean => form.horizonCenterId === null);
    // Add 'select all' to list of options
    locationListItems.unshift({ id: SELECT_ALL, name: SELECT_ALL, appKey: SELECT_ALL });
    // Add 'blank' to list of options if patient found without location
    blankLocationFound.includes(true) && locationListItems.push({ id: BLANK, name: BLANK, appKey: BLANK });

    return (
      <Fragment>
        {locationListItems.length > 0 && (
          <DropDownField
            key={locationFilterId || 'default'}
            inputName={'Location Filter'}
            placeholder={'Location Filter'}
            defaultValue={
              locationFilterId === SELECT_ALL_ID
                ? SELECT_ALL
                : locationFilterId === BLANK_ID
                ? BLANK
                : getLocationString(locationFilterId)
            }
            options={locationListItems}
            className="location-dropdown"
            onChange={(e): void => {
              // Convert the display name back to the API value
              let updatedLocationId;
              if (e.target.value === SELECT_ALL) {
                updatedLocationId = SELECT_ALL_ID;
              } else if (e.target.value === BLANK) {
                updatedLocationId = BLANK_ID;
              } else {
                const location = allowedLocations.find((item: any) => item.alias === e.target.value);
                updatedLocationId = location ? location.id : '';
              }

              if (updatedLocationId !== undefined) {
                handleLocationFilterChange(e.target.value, updatedLocationId);
              }
            }}
          />
        )}
      </Fragment>
    );
  };

  const renderTableRow = (formRow: ReviewRequiredForm): JSX.Element[] => {
    return mapFormData({ ...formRow }).map((item: ItemProps, index): JSX.Element => {
      const formUrl = generateFormUrl(formRow.formType, formRow.patientId, showNewRego, formRow.hasDataConflicts);
      const patientUrl = `/navigator/patient/${formRow.patientId}/summary`;
      const url = index === 0 ? patientUrl : formUrl;
      return (
        <TableCell key={index}>
          <Link
            style={{ color: 'black', textDecoration: 'none' }}
            className={classnames('link', { [`${item.linkClass}`]: item.linkClass })}
            data-test-id={`row-${item.name}`}
            to={{ pathname: url, state: { from: location.pathname } }}
            onClick={(e): void => {
              e.stopPropagation();
              sessionStorage.setItem('referringPage', '/review-forms');
            }}>
            <span className={classnames('form', { [`${item.valueClass}`]: item.valueClass })}>{item.value}</span>
            {item.otherValue && (
              <span className={classnames({ [`${item.otherValueClass}`]: item.otherValueClass })}>
                {item.otherValue}
              </span>
            )}
          </Link>
        </TableCell>
      );
    });
  };

  const renderTableData = (): JSX.Element[] => {
    if (!forms.length) return [];

    const filteredForms = forms.filter((formData: ReviewRequiredForm) => formData.formType === formTypes.registration);
    const formsToParse = isUs() ? filteredForms : forms;

    const processedForms = formsToParse.map((form: ReviewRequiredForm) => {
      const { horizonCenterId } = form;
      const locationCode = horizonCenterId ? formatLocation(horizonCenterId, allowedLocations) : '';
      return { ...form, locationCode };
    });

    return processedForms.map((form: ReviewRequiredForm, index: number): JSX.Element => {
      return <StyledTableRow key={index}>{renderTableRow(form)}</StyledTableRow>;
    });
  };

  // Handle loading and error states
  if (loading && !formData) {
    return <LoadingSpinner loadingText={'Loading forms'} />;
  }

  if (error) {
    return <div>Error loading forms: {error.message}</div>;
  }

  return (
    <div style={{ borderTop: `6px solid ${theme.palette.primary.main}` }} className="review-forms-container">
      <div className="filter-title-container">
        <div className="review-forms-title">{REVIEW_FORMS}</div>
        {renderLocationFilter()}
      </div>
      <div className="review-forms-table-container">
        <Table className="review-forms-table">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map(
                (header): JSX.Element => (
                  <StyledTableCell key={header}>{header}</StyledTableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <tbody>{renderTableData()}</tbody>
        </Table>
      </div>
      {!loading && forms.length === 0 && <div className="no-patients">{NO_PATIENTS_MESSAGE}</div>}
      {forms.length !== 0 && (
        <Pagination
          totalRecords={totalCount}
          page={pagination?.currentPage}
          onPageChange={handlePaginationChange}
          pageLimit={DEFAULT_PAGINATION_LIMIT}
          loading={loading}
        />
      )}
    </div>
  );
};

export default withRouter(ReviewFormsTable);
